<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="t('Conciliação de Deduções de Colaboradores')"
    :steps="wizardStepsConfig"
    size="xlarge"
    :show-summary="currentStepIndex >= 2"
    :summary-items="summaryItems"
    :reset-on-close="true"
    :loading="isProcessing"
    @close="handleClose"
    @cancel="handleClose"
    @step-change="handleStepChange"
    @finish="handleFinish">

    <!-- Step 0: Current Deductions Status -->
    <template #step-0>
      <div class="wizard-step-content">
        <div class="step-header">
          <h4>{{ t('Status das Deduções') }}</h4>
          <p class="step-subtitle">
            {{ t('Revise as deduções já marcadas para este período de folha') }}
          </p>
        </div>

        <div v-if="isLoadingDeductions" class="loading-container">
          <spinner data-testid="payroll-deductions-loading-status" />
        </div>

        <div v-else>
          <!-- KPI Cards -->
          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-icon info">
                <i class="pi pi-users"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value" data-testid="payroll-deductions-employees-count">{{ uniqueEmployeesWithDeductions }}</span>
                <span class="kpi-label">{{ t('Colaboradores com Deduções') }}</span>
              </div>
            </div>

            <div class="kpi-card">
              <div class="kpi-icon success">
                <i class="pi pi-file-text"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value" data-testid="payroll-deductions-selected-count">{{ deductionsSelectedCount }}</span>
                <span class="kpi-label">{{ t('Deduções Selecionadas') }}</span>
              </div>
            </div>

            <div class="kpi-card">
              <div class="kpi-icon warning">
                <i class="pi pi-dollar"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value" data-testid="payroll-deductions-total-amount">{{ n(currentDeductionsTotal, 'currency') }}</span>
                <span class="kpi-label">{{ t('Total a Descontar') }}</span>
              </div>
            </div>
          </div>

          <!-- Current Selected Deductions by Employee (uses selectedItemsFromBackend) -->
          <div v-if="employeesWithSelectedDeductions.length > 0" class="current-deductions-section">
            <h5 class="section-title">{{ t('Deduções Selecionadas por Colaborador') }}</h5>

            <div v-for="employee in employeesWithSelectedDeductions" :key="employee.employee_id" class="employee-section">
              <div class="employee-header">
                <div class="employee-info">
                  <i class="pi pi-user"></i>
                  <span class="employee-name">{{ employee.employee_name }}</span>
                </div>
                <div class="employee-total">
                  <span class="total-label">{{ t('Total Descontado') }}:</span>
                  <span class="total-value">{{ n(getSelectedEmployeeTotal(employee), 'currency') }}</span>
                </div>
              </div>

              <div class="deductions-list">
                <div v-for="deduction in employee.deductions" :key="deduction.id" class="deduction-item editable">
                  <div class="deduction-info">
                    <span class="deduction-title">{{ deduction.title }}</span>
                  </div>
                  <div class="deduction-amounts">
                    <div class="amount-row">
                      <span class="amount-label">{{ t('Valor Original') }}:</span>
                      <span class="amount-value">{{ n(deduction.amount, 'currency') }}</span>
                    </div>
                    <div class="amount-row edit-row">
                      <span class="amount-label">{{ t('Valor a Descontar') }}:</span>
                      <div class="edit-input-group">
                        <input-currency
                          :modelValue="selectedDiscounts[deduction.id] ?? deduction.amount_discounted"
                          :min="0"
                          :max="deduction.amount"
                          :elem-id="`payroll-deductions-edit-discount-${deduction.id}`"
                          :label="''"
                          :placeholder="t('0,00')"
                          class="edit-input"
                          :data-testid="`payroll-deductions-edit-discount-${deduction.id}`"
                          @update:modelValue="(v: number | null) => handleSelectedDiscountEdit(deduction.id, v)"
                        />
                        <app-button
                          icon="pi pi-times"
                          severity="danger"
                          text
                          rounded
                          size="small"
                          :title="t('Remover desconto')"
                          :data-testid="`payroll-deductions-remove-${deduction.id}`"
                          @click="handleRemoveSelectedDeduction(deduction.id)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state" data-testid="payroll-deductions-empty-state">
            <i class="pi pi-check-circle"></i>
            <h4>{{ t('Nenhuma dedução selecionada') }}</h4>
            <p>{{ t('Avance para o próximo passo para selecionar deduções para desconto neste período.') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 1: Select Deductions to Discount -->
    <template #step-1>
      <div class="wizard-step-content">
        <div class="step-header">
          <h4>{{ t('Selecionar Deduções para Desconto') }}</h4>
          <p class="step-subtitle">
            {{ t('Selecione as deduções e os valores a descontar neste período') }}
          </p>
        </div>

        <div v-if="isLoadingDeductions" class="loading-container">
          <spinner data-testid="payroll-deductions-loading-select" />
        </div>

        <div v-else>
          <inline-message severity="warn" :closable="false" class="info-message" data-testid="payroll-deductions-status-info">
            <i class="pi pi-exclamation-triangle"></i>
            {{ t('O status das deduções será atualizado automaticamente (aberto → parcial → pago) conforme os descontos são aplicados.') }}
          </inline-message>

          <!-- EmployeeDiscountCard components for each employee -->
          <div v-if="employeesWithDeductions.length > 0" class="employees-list">
            <EmployeeDiscountCard
              v-for="employee in employeesWithDeductions"
              :key="employee.employee_id"
              :employee="{ employee_id: employee.employee_id, employee_name: employee.employee_name }"
              :items="(employee.deductions as unknown as DiscountCardItem[])"
              :selected-discounts="(selectedDiscounts as unknown as Record<Id, number>)"
              @update:discount="handleDiscountUpdate"
            />
          </div>

          <div v-else class="empty-state" data-testid="payroll-deductions-no-open-state">
            <i class="pi pi-check-circle"></i>
            <h4>{{ t('Nenhuma dedução disponível') }}</h4>
            <p>{{ t('Não há deduções abertas ou parciais para os colaboradores deste período.') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 2: Confirm Selection -->
    <template #step-2>
      <div class="wizard-step-content">
        <div class="step-header">
          <h4>{{ t('Confirmar Seleção') }}</h4>
          <p class="step-subtitle">
            {{ t('Revise e confirme as deduções selecionadas para desconto') }}
          </p>
        </div>

        <!-- Final Summary -->
        <div class="final-summary">
          <div class="summary-card">
            <div class="summary-header">
              <i class="pi pi-check-circle"></i>
              <h5>{{ t('Resumo da Seleção') }}</h5>
            </div>
            <div class="summary-body">
              <div class="summary-row">
                <span class="label">{{ t('Colaboradores com deduções') }}:</span>
                <span class="value" data-testid="payroll-deductions-summary-employees">{{ uniqueEmployeesWithDeductions }}</span>
              </div>
              <div class="summary-row">
                <span class="label">{{ t('Deduções selecionadas') }}:</span>
                <span class="value highlight" data-testid="payroll-deductions-summary-selected">{{ deductionsSelectedCount }}</span>
              </div>
              <div class="summary-row">
                <span class="label">{{ t('Valor das deduções selecionadas') }}:</span>
                <span class="value highlight" data-testid="payroll-deductions-summary-amount">{{ n(currentDeductionsTotal, 'currency') }}</span>
              </div>
              <div class="summary-row total">
                <span class="label">{{ t('Total a descontar neste período') }}:</span>
                <span class="value" data-testid="payroll-deductions-summary-total">{{ n(currentDeductionsTotal, 'currency') }}</span>
              </div>
            </div>
          </div>

          <!-- Selected Items Preview -->
          <div v-if="deductionsSelectedCount > 0" class="selected-preview">
            <h5 class="section-title">{{ t('Deduções Selecionadas') }}</h5>
            <DataTable
              :config="selectedDeductionsTableConfig"
              :data="selectedDeductionsList"
              class="preview-table compact"
              data-testid="payroll-deductions-preview-table"
            />
          </div>

          <inline-message v-else severity="warn" :closable="false" data-testid="payroll-deductions-none-warning">
            {{ t('Nenhuma dedução selecionada. Clique em "Voltar" para selecionar deduções, ou "Finalizar" para continuar sem descontos.') }}
          </inline-message>
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import { useEmployeeDeductions } from '@/composables/useEmployeeDeductions';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import EmployeeDiscountCard from './shared/EmployeeDiscountCard.vue';

type Id = number | string;

interface DeductionItem {
  id: Id;
  title: string;
  amount: number;
  amount_remaining: number;
  amount_discounted?: number;
  employee_id: Id;
  employee_name: string;
  created_at?: string | null;
  [key: string]: unknown;
}

// Mirror of EmployeeDiscountCard's DiscountItem to satisfy strict prop typing
interface DiscountCardItem {
  id: Id;
  title: string;
  amount: number;
  amount_remaining: number;
  source_type?: string;
  request_date?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface SelectedItemFromBackend {
  deduction_id: Id;
  amount: number;
  title: string;
  deduction_amount: number;
  employee_id?: Id;
  employee_name?: string;
  [key: string]: unknown;
}

interface DeductionsApiResponse {
  deductions: DeductionItem[];
  summary_by_employee: Array<{ employee_id: Id; employee_name: string; total: number; count: number }>;
  total_open: number;
  selected_items?: SelectedItemFromBackend[];
}

interface EmployeeBucket {
  employee_id: Id;
  employee_name: string;
  deductions: DeductionItem[];
}

interface DiscountUpdatePayload {
  itemId: Id;
  amount: number | null;
}

interface SelectedPreviewRow {
  id: Id;
  title: string;
  amount: number;
  employee_name: string;
  employee_id?: Id;
  amount_discounted: number;
}

interface DiscountEntry {
  deduction_id: string;
  amount: number;
}

interface CompletedPayload {
  deductionsProcessed: number;
  deductionsRemoved: number;
  selections: Record<string, number>;
  discounts: DiscountEntry[];
}

interface Props {
  visible?: boolean;
  periodId: number;
  franchiseId: number;
  usersMap?: Record<string, unknown>;
  initialSelections?: Record<string, number>;
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  usersMap: () => ({}),
  initialSelections: () => ({}),
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'completed': [payload: CompletedPayload];
  'closed': [];
  'update:selections': [selections: Record<string, number>];
}>();

const { t, n } = useI18n();
const {
  getOpenDeductionsForPeriod,
  processDiscounts,
} = useEmployeeDeductions();

// formatDateLocale kept for parity with prior JS (used inside formatDate helper preserved below)
const { formatDate: formatDateLocale } = useDateFormatter();

// Refs
const isVisible = ref<boolean>(false);
const currentStepIndex = ref<number>(0);
const isProcessing = ref<boolean>(false);

// Data
const deductionsData = ref<DeductionsApiResponse>({
  deductions: [],
  summary_by_employee: [],
  total_open: 0
});
const selectedDiscounts = ref<Record<string, number>>({}); // { deduction_id: amount }
const selectedItemsFromBackend = ref<SelectedItemFromBackend[]>([]); // Already selected items from backend (for resuming/editing)
const removedDeductions = ref<Id[]>([]); // Track deductions removed by user (to delete from backend)

// Loading states
const isLoadingDeductions = ref<boolean>(false);

// Wizard steps configuration (3 steps)
const wizardStepsConfig = computed(() => [
  {
    title: t('Status'),
    description: t('Deduções atuais'),
    icon: 'pi pi-chart-bar',
    validate: () => true
  },
  {
    title: t('Selecionar'),
    description: t('Deduções para desconto'),
    icon: 'pi pi-list',
    validate: () => true
  },
  {
    title: t('Confirmar'),
    description: t('Finalizar seleção'),
    icon: 'pi pi-check',
    validate: () => true
  }
]);

// Summary items for wizard header
const summaryItems = computed(() => [
  {
    icon: 'pi pi-calendar',
    label: t('Período'),
    value: `#${props.periodId}`
  },
  {
    icon: 'pi pi-file-text',
    label: t('Deduções'),
    value: `${deductionsSelectedCount.value} (${n(currentDeductionsTotal.value, 'currency')})`
  }
]);

// Computed: Group OPEN deductions by employee (for Step 1 - selecting new deductions)
const employeesWithDeductions = computed<EmployeeBucket[]>(() => {
  const byEmployee: Record<string, EmployeeBucket> = {};
  deductionsData.value.deductions.forEach((d) => {
    const key = String(d.employee_id);
    if (!byEmployee[key]) {
      byEmployee[key] = {
        employee_id: d.employee_id,
        employee_name: d.employee_name,
        deductions: []
      };
    }
    byEmployee[key].deductions.push(d);
  });
  return Object.values(byEmployee);
});

// Computed: Group SELECTED deductions by employee (for Step 0 - showing current status)
// Uses selectedItemsFromBackend which contains already-processed deductions even if they're now PAID
const employeesWithSelectedDeductions = computed<EmployeeBucket[]>(() => {
  const byEmployee: Record<string, EmployeeBucket> = {};
  selectedItemsFromBackend.value.forEach((item) => {
    const employeeId = item.employee_id;
    if (employeeId === undefined || employeeId === null) return;

    const key = String(employeeId);
    if (!byEmployee[key]) {
      byEmployee[key] = {
        employee_id: employeeId,
        employee_name: item.employee_name || 'Unknown',
        deductions: []
      };
    }
    byEmployee[key].deductions.push({
      id: item.deduction_id,
      title: item.title,
      amount: item.deduction_amount,
      amount_remaining: 0, // Already applied, so remaining is effectively what was discounted
      amount_discounted: item.amount,
      employee_id: employeeId,
      employee_name: item.employee_name || 'Unknown',
      created_at: null // Not available from selected_items
    });
  });
  return Object.values(byEmployee);
});

// Computed: Current deductions
const currentDeductions = computed<DeductionItem[]>(() => deductionsData.value.deductions);

// Computed: Current deductions total
const currentDeductionsTotal = computed<number>(() => {
  return Object.values(selectedDiscounts.value).reduce((sum, amount) => sum + (parseFloat(String(amount ?? 0)) || 0), 0);
});

// Computed: Unique employees with selected deductions (uses backend selected items)
const uniqueEmployeesWithDeductions = computed<number>(() => {
  return employeesWithSelectedDeductions.value.length;
});

// Computed: Deductions selected count
const deductionsSelectedCount = computed<number>(() => {
  return Object.values(selectedDiscounts.value).filter((amount) => amount > 0).length;
});

// Computed: Selected deductions list (for Step 2 confirmation)
// Uses selectedItemsFromBackend which has complete data even for deductions that are now PAID
const selectedDeductionsList = computed<SelectedPreviewRow[]>(() => {
  // Use selectedItemsFromBackend as primary source - it has all the data we need
  if (selectedItemsFromBackend.value.length > 0) {
    return selectedItemsFromBackend.value.map((item) => ({
      id: item.deduction_id,
      title: item.title,
      amount: item.deduction_amount,
      employee_name: item.employee_name ?? '',
      employee_id: item.employee_id,
      amount_discounted: item.amount
    }));
  }

  // Fallback: try to match against currentDeductions (for newly selected items)
  return currentDeductions.value
    .filter((deduction) => (selectedDiscounts.value[String(deduction.id)] ?? 0) > 0)
    .map((deduction) => ({
      id: deduction.id,
      title: deduction.title,
      amount: deduction.amount,
      employee_name: deduction.employee_name,
      employee_id: deduction.employee_id,
      amount_discounted: selectedDiscounts.value[String(deduction.id)] ?? 0
    }));
});

// DataTable config for Step 2 confirmation preview (uses src/grid/DataTable.vue)
const selectedDeductionsTableConfig = computed(() => ({
  columns: [
    { data: 'employee_name', title: t('Colaborador') },
    { data: 'title', title: t('Título') },
    { data: 'amount', title: t('Total'), renderType: 'currency' },
    { data: 'amount_discounted', title: t('Valor a Descontar'), renderType: 'currency' },
  ],
  pagination: selectedDeductionsList.value.length > 5,
  pageLength: 5,
  showFilters: false,
  sortable: false,
  size: 'small',
}));

// Helper: Get employee total
const getEmployeeTotal = (employee: EmployeeBucket): number => {
  return employee.deductions
    .filter((d) => (selectedDiscounts.value[String(d.id)] ?? 0) > 0)
    .reduce((sum, d) => sum + (parseFloat(String(selectedDiscounts.value[String(d.id)] ?? 0)) || 0), 0);
};

// Helper: Get total for selected employee (uses selectedDiscounts for reactivity)
const getSelectedEmployeeTotal = (employee: EmployeeBucket): number => {
  return employee.deductions.reduce((sum, d) => {
    // Use selectedDiscounts if available for reactivity, fall back to amount_discounted
    const amount = selectedDiscounts.value[String(d.id)] ?? d.amount_discounted ?? 0;
    return sum + (parseFloat(String(amount)) || 0);
  }, 0);
};

// Helper: Format date
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return formatDateLocale(date);
};

// Load current deductions
const loadCurrentDeductions = async (): Promise<void> => {
  isLoadingDeductions.value = true;
  try {
    log('EmployeeDeductionsConciliationWizard', 'loadCurrentDeductions', '📥 START - Loading deductions from API', {
      periodId: props.periodId,
      franchiseId: props.franchiseId
    });

    const data = (await getOpenDeductionsForPeriod(props.periodId)) as unknown as DeductionsApiResponse;
    deductionsData.value = data;

    // Store selected_items for display (even if original deduction is now PAID)
    selectedItemsFromBackend.value = data.selected_items || [];

    log('EmployeeDeductionsConciliationWizard', 'loadCurrentDeductions', '📦 API Response received', {
      deductionsCount: data.deductions?.length || 0,
      selectedItemsCount: data.selected_items?.length || 0,
      selectedItemsFromBackendCount: selectedItemsFromBackend.value.length,
      totalOpen: data.total_open
    });

    // If we have selected_items from backend AND no selections were passed via props,
    // restore them (this handles the case of resuming/editing a period)
    if (data.selected_items && data.selected_items.length > 0 && Object.keys(selectedDiscounts.value).length === 0) {
      const restoredSelections: Record<string, number> = {};
      data.selected_items.forEach((item) => {
        const key = String(item.deduction_id);
        restoredSelections[key] = item.amount;
      });
      selectedDiscounts.value = restoredSelections;

      log('EmployeeDeductionsConciliationWizard', 'loadCurrentDeductions', '✅ Restored selections from selected_items', {
        restoredCount: Object.keys(restoredSelections).length,
        restoredKeys: Object.keys(restoredSelections),
        restoredValues: Object.values(restoredSelections)
      });
    }

    log('EmployeeDeductionsConciliationWizard', 'loadCurrentDeductions', '✅ END - Load complete', {
      deductionsCount: data.deductions.length,
      selectedDiscountsCount: Object.keys(selectedDiscounts.value).length,
      selectedItemsFromBackendCount: selectedItemsFromBackend.value.length,
      employeesWithSelectedDeductionsCount: employeesWithSelectedDeductions.value.length,
      currentTotal: currentDeductionsTotal.value
    });
  } catch (error: unknown) {
    console.error('Error loading deductions:', error);
    log('EmployeeDeductionsConciliationWizard', 'loadCurrentDeductions', '❌ Error', error);
    const message = error instanceof Error ? error.message : t('Erro ao carregar deduções');
    eventBus.emit('globalMessage.error', {
      message: message || t('Erro ao carregar deduções'),
      duration: 5000
    });
  } finally {
    isLoadingDeductions.value = false;
  }
};

// Handle discount update (from Step 1 - EmployeeDiscountCard)
const handleDiscountUpdate = ({ itemId, amount }: DiscountUpdatePayload): void => {
  log('EmployeeDeductionsConciliationWizard', 'handleDiscountUpdate', 'Updating discount', { itemId, amount });

  const key = String(itemId);
  if (amount === null) {
    delete selectedDiscounts.value[key];
  } else {
    selectedDiscounts.value[key] = amount;
  }
};

// Handle editing a selected deduction (from Step 0)
const handleSelectedDiscountEdit = (deductionId: Id, newAmount: number | null): void => {
  log('EmployeeDeductionsConciliationWizard', 'handleSelectedDiscountEdit', 'Editing selected discount', {
    deductionId,
    newAmount,
    previousAmount: selectedDiscounts.value[String(deductionId)]
  });

  const key = String(deductionId);
  selectedDiscounts.value[key] = parseFloat(String(newAmount ?? 0)) || 0;

  // Also update the selectedItemsFromBackend for display consistency
  const itemIndex = selectedItemsFromBackend.value.findIndex((item) => item.deduction_id === deductionId);
  if (itemIndex !== -1) {
    selectedItemsFromBackend.value[itemIndex].amount = parseFloat(String(newAmount ?? 0)) || 0;
  }

  log('EmployeeDeductionsConciliationWizard', 'handleSelectedDiscountEdit', 'Updated', {
    selectedDiscounts: selectedDiscounts.value,
    currentTotal: currentDeductionsTotal.value
  });
};

// Handle removing a selected deduction (from Step 0)
const handleRemoveSelectedDeduction = (deductionId: Id): void => {
  log('EmployeeDeductionsConciliationWizard', 'handleRemoveSelectedDeduction', 'Removing deduction', {
    deductionId,
    previousAmount: selectedDiscounts.value[String(deductionId)]
  });

  const key = String(deductionId);

  // Track as removed (to delete from backend on save)
  if (!removedDeductions.value.includes(deductionId)) {
    removedDeductions.value.push(deductionId);
  }

  // Remove from selectedDiscounts
  delete selectedDiscounts.value[key];

  // Remove from selectedItemsFromBackend
  selectedItemsFromBackend.value = selectedItemsFromBackend.value.filter(
    (item) => item.deduction_id !== deductionId
  );

  log('EmployeeDeductionsConciliationWizard', 'handleRemoveSelectedDeduction', 'Removed', {
    remainingCount: selectedItemsFromBackend.value.length,
    removedDeductions: removedDeductions.value,
    currentTotal: currentDeductionsTotal.value
  });
};

// Handle step change
const handleStepChange = (stepIndex: number): void => {
  currentStepIndex.value = stepIndex;

  // Load data when entering step 0
  if (stepIndex === 0 && currentDeductions.value.length === 0) {
    loadCurrentDeductions();
  }
};

// Handle wizard close
const handleClose = (): void => {
  log('EmployeeDeductionsConciliationWizard', 'handleClose', 'Closing wizard', {
    selectedDiscounts: selectedDiscounts.value,
    selectedCount: Object.keys(selectedDiscounts.value).length,
    total: Object.values(selectedDiscounts.value).reduce((sum, amount) => sum + (parseFloat(String(amount ?? 0)) || 0), 0)
  });

  // Emit selections back to parent before closing
  emit('update:selections', { ...selectedDiscounts.value });
  isVisible.value = false;
  emit('update:visible', false);
  emit('closed');
  resetWizard();
};

// Handle wizard finish
const handleFinish = async (): Promise<void> => {
  isProcessing.value = true;

  try {
    log('EmployeeDeductionsConciliationWizard', 'handleFinish', 'Processing', {
      deductionsCount: deductionsSelectedCount.value,
      deductionsTotal: currentDeductionsTotal.value,
      removedCount: removedDeductions.value.length
    });

    // Build discounts array - include both active discounts and removed ones (with amount: 0)
    const discounts: DiscountEntry[] = Object.entries(selectedDiscounts.value)
      .filter(([_, amount]) => amount > 0)
      .map(([deductionId, amount]) => ({
        deduction_id: String(deductionId),
        amount: parseFloat(String(amount))
      }));

    // Add removed deductions with amount: 0 (to delete from backend)
    removedDeductions.value.forEach((deductionId) => {
      discounts.push({
        deduction_id: String(deductionId),
        amount: 0
      });
    });

    log('EmployeeDeductionsConciliationWizard', 'handleFinish', 'Sending discounts', {
      activeCount: discounts.filter((d) => d.amount > 0).length,
      removedCount: discounts.filter((d) => d.amount === 0).length,
      discounts
    });

    if (discounts.length > 0) {
      await processDiscounts(props.periodId, discounts);

      eventBus.emit('globalMessage.success', {
        message: t('Deduções processadas com sucesso'),
        duration: 3000
      });
    }

    emit('completed', {
      deductionsProcessed: discounts.filter((d) => d.amount > 0).length,
      deductionsRemoved: discounts.filter((d) => d.amount === 0).length,
      selections: { ...selectedDiscounts.value },
      discounts
    });

    eventBus.emit('payroll.deductions.updated', { periodId: props.periodId });
    handleClose();

  } catch (error: unknown) {
    console.error('Error processing deductions:', error);
    const message = error instanceof Error ? error.message : t('Erro ao processar deduções');
    eventBus.emit('globalMessage.error', {
      message: message || t('Erro ao processar deduções'),
      duration: 5000
    });
  } finally {
    isProcessing.value = false;
  }
};

// Reset wizard state
const resetWizard = (): void => {
  // Initialize selections from machine context (passed from parent)
  selectedDiscounts.value = { ...props.initialSelections };
  selectedItemsFromBackend.value = []; // Clear backend items on reset
  removedDeductions.value = []; // Clear removed deductions on reset
  currentStepIndex.value = 0;

  log('EmployeeDeductionsConciliationWizard', 'resetWizard', 'Initialized with selections', {
    count: Object.keys(props.initialSelections).length,
    selections: props.initialSelections
  });
};

// Watch for visibility changes
watch(() => props.visible, async (newValue) => {
  isVisible.value = newValue;
  if (newValue) {
    log('EmployeeDeductionsConciliationWizard', 'watch:visible', 'Wizard opened, loading data');
    resetWizard();
    await loadCurrentDeductions();
  }
});
</script>

<style scoped>
.wizard-step-content {
  padding: 1rem 0;
}

.step-header {
  margin-bottom: 1.5rem;
}

.step-header h4 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
  font-size: 1.25rem;
}

.step-subtitle {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.kpi-icon.info {
  background: var(--blue-50);
  color: var(--blue-500);
}

.kpi-icon.success {
  background: var(--green-50);
  color: var(--green-500);
}

.kpi-icon.warning {
  background: var(--orange-50);
  color: var(--orange-500);
}

.kpi-content {
  display: flex;
  flex-direction: column;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
}

.kpi-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

/* Section styling */
.section-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.current-deductions-section {
  margin-top: 1.5rem;
}

.employee-section {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  margin-bottom: 1rem;
  overflow: hidden;
}

.employee-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.employee-info i {
  color: var(--primary-color);
  font-size: 1.25rem;
}

.employee-name {
  font-weight: 600;
  color: var(--text-color);
}

.employee-total {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.total-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.total-value {
  font-weight: 700;
  font-size: 1.125rem;
  color: var(--primary-color);
}

.deductions-list {
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.deduction-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 6px;
}

.deduction-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.deduction-title {
  font-weight: 600;
  color: var(--text-color);
}

.deduction-date {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.deduction-amounts {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: right;
}

.amount-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.amount-label {
  color: var(--text-color-secondary);
}

.amount-value {
  font-weight: 600;
  color: var(--text-color);
}

.amount-remaining {
  color: var(--orange-600);
}

.amount-row.selected .amount-value {
  color: var(--primary-color);
}

.amount-selected {
  color: var(--primary-color);
}

/* Editable deduction items */
.deduction-item.editable {
  background: var(--surface-50);
  border: 1px solid var(--primary-200);
}

.edit-row {
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.edit-input-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.edit-input {
  width: 140px;
}

.edit-input :deep(.p-inputnumber) {
  width: 100%;
}

.edit-input :deep(.p-inputnumber-input) {
  width: 100%;
  text-align: right;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 2px dashed var(--surface-200);
}

.empty-state i {
  font-size: 3rem;
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

.empty-state h4 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.empty-state p {
  margin: 0;
  color: var(--text-color-secondary);
}

/* Info message */
.info-message {
  margin-bottom: 1.5rem;
}

/* Employees list */
.employees-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Final summary */
.final-summary {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.summary-header i {
  font-size: 1.25rem;
  color: var(--green-500);
}

.summary-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.summary-body {
  padding: 1.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--surface-100);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.total {
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 2px solid var(--surface-200);
  border-bottom: none;
  font-size: 1.1rem;
}

.summary-row .label {
  color: var(--text-color-secondary);
}

.summary-row .value {
  font-weight: 600;
  color: var(--text-color);
}

.summary-row .value.highlight {
  color: var(--orange-600);
}

.summary-row.total .label,
.summary-row.total .value {
  font-weight: 700;
  color: var(--text-color);
}

/* Selected preview */
.selected-preview {
  margin-top: 1rem;
}

.preview-table.compact {
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
